/**
 * Hard-constraint checks for crew minimum, curfew, customs, and SimBrief
 * weight. Every threshold comes from `company.opsDesk` or aircraft data.
 * Cargo on the ballpark is extra freight only — SimBrief's per-pax bags are
 * counted once, not added again from the ticket cargo figure.
 */
import { SIMBRIEF_INCLUDED_BAG_LB, simbriefExtraFreightLb } from "./simbrief";
import {
  formatQuietWindow,
  hoursForAirport,
  icaoKey,
  isInQuietHours,
  type AirportHours,
} from "./curfew";
import { resolveCrewComplement } from "./opsDeskSettings";
import { evaluateDeice, type DeiceVerdict } from "./deiceOps";
import { evaluateConvectiveHold, type ConvectiveHoldVerdict } from "./convectiveHold";
import type { FieldWx } from "./weather";
import type { AwcHazardBrief } from "./awcHazards";
import type {
  AircraftSpec,
  OpsAirportOps,
  OpsConstraintEnforcement,
  OpsDeskSettings,
} from "./types";

export type ConstraintCode = "under_crewed" | "curfew" | "customs" | "overweight" | "deice" | "convective_hold";

export type ConstraintHit = {
  code: ConstraintCode;
  severity: OpsConstraintEnforcement;
  message: string;
};

const NOTE_MARKERS = ["CREW MIN", "OPS CURFEW", "CUSTOMS", "WEIGHT", "DE-ICE", "CONVECTIVE HOLD"] as const;

export function findAirportOps(
  ops: Pick<OpsDeskSettings, "airports"> | null | undefined,
  icao: string | null | undefined,
): OpsAirportOps | undefined {
  const key = icaoKey(icao);
  if (!key || !ops) return undefined;
  return ops.airports.find((row) => row.icao === key);
}

/** Company row, then company default, then the built-in quiet-hours table. */
export function effectiveAirportHours(
  icao: string | null | undefined,
  ops?: OpsDeskSettings | null,
): AirportHours {
  const builtin = hoursForAirport(icao);
  const row = findAirportOps(ops, icao);
  if (row?.curfew) {
    return {
      icao: builtin.icao,
      timeZone: builtin.timeZone,
      quietStartMin: row.curfew.quietStartMin,
      quietEndMin: row.curfew.quietEndMin,
      unrestricted: row.curfew.unrestricted,
      note: row.notes || "Company airport curfew",
    };
  }
  if (ops?.curfewUseCompanyDefault) {
    const window = ops.defaultCurfew;
    return {
      icao: builtin.icao,
      timeZone: builtin.timeZone,
      quietStartMin: window.quietStartMin,
      quietEndMin: window.quietEndMin,
      unrestricted: window.unrestricted,
      note: "Company default curfew",
    };
  }
  return builtin;
}

export function hoursResolverFor(ops?: OpsDeskSettings | null): (icao: string | null | undefined) => AirportHours {
  return (icao) => effectiveAirportHours(icao, ops);
}

export type CrewMinVerdict = {
  complement: "dual" | "single";
  required: number;
  assigned: number;
  ok: boolean;
  severity: "ok" | OpsConstraintEnforcement;
  message: string;
};

export function uniqueCrewIds(ids: ReadonlyArray<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    const id = String(raw ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function evaluateCrewMinimum(input: {
  ops: Pick<OpsDeskSettings, "dualPilotDefault" | "singlePilotEnabled" | "crewMinEnforcement">;
  tailSinglePilot?: boolean | null;
  crewIds: ReadonlyArray<string | null | undefined>;
  registration?: string;
}): CrewMinVerdict {
  const complement = resolveCrewComplement(input.ops, input.tailSinglePilot);
  const required = complement === "single" ? 1 : 2;
  const assigned = uniqueCrewIds(input.crewIds).length;
  if (assigned >= required) {
    return { complement, required, assigned, ok: true, severity: "ok", message: "" };
  }
  const who = (input.registration ?? "").trim() || "This tail";
  const message = `CREW MIN  ${who} is ${complement === "single" ? "single-pilot" : "dual-pilot"}. ${assigned} of ${required} assigned.`;
  return {
    complement,
    required,
    assigned,
    ok: false,
    severity: input.ops.crewMinEnforcement === "block" ? "block" : "flag",
    message,
  };
}

export type CurfewVerdict = {
  legal: boolean;
  depQuiet: boolean;
  arrQuiet: boolean;
  severity: "ok" | OpsConstraintEnforcement;
  message: string;
};

export function evaluateCurfew(input: {
  ops: OpsDeskSettings;
  originIcao: string;
  destIcao: string;
  departure: Date | null;
  eteMin: number;
}): CurfewVerdict {
  const quiet = { legal: true, depQuiet: false, arrQuiet: false, severity: "ok" as const, message: "" };
  if (!input.departure || !Number.isFinite(input.departure.getTime())) return quiet;
  const hoursFor = hoursResolverFor(input.ops);
  const ete = Number.isFinite(input.eteMin) ? Math.max(0, input.eteMin) : 0;
  const arrival = new Date(input.departure.getTime() + ete * 60_000);
  const depHours = hoursFor(input.originIcao);
  const arrHours = hoursFor(input.destIcao);
  const depQuiet = isInQuietHours(input.originIcao, input.departure, depHours);
  const arrQuiet = isInQuietHours(input.destIcao, arrival, arrHours);
  if (!depQuiet && !arrQuiet) return quiet;
  const from = icaoKey(input.originIcao);
  const to = icaoKey(input.destIcao);
  const bits: string[] = [];
  if (depQuiet) bits.push(`dep ${from} (${formatQuietWindow(depHours)})`);
  if (arrQuiet) bits.push(`arr ${to} (${formatQuietWindow(arrHours)})`);
  return {
    legal: false,
    depQuiet,
    arrQuiet,
    severity: input.ops.curfewEnforcement === "block" ? "block" : "flag",
    message: `OPS CURFEW  ${from}–${to} overlaps quiet hours (${bits.join("; ")}).`,
  };
}

export type CustomsField = {
  icao: string;
  country?: string;
};

export type CustomsVerdict = {
  required: boolean;
  ok: boolean;
  severity: "ok" | OpsConstraintEnforcement;
  message: string;
  suggestedStopIcao?: string;
};

function countryOf(fields: readonly CustomsField[], icao: string): string {
  const key = icaoKey(icao);
  const row = fields.find((field) => icaoKey(field.icao) === key);
  return String(row?.country ?? "").trim();
}

function clearsCustoms(ops: OpsDeskSettings, icao: string): boolean {
  return findAirportOps(ops, icao)?.customsAvailable === true;
}

/**
 * Country change is read from the Destinations list. A required stop is a
 * field the company marked customs-capable. Unknown customs is not a guess.
 */
export function evaluateCustoms(input: {
  ops: OpsDeskSettings;
  originIcao: string;
  destIcao: string;
  scenery: readonly CustomsField[];
  stopIcaos?: readonly string[];
}): CustomsVerdict {
  const ok = { required: false, ok: true, severity: "ok" as const, message: "" };
  if (!input.ops.customsOnCountryChange) return ok;
  const originCountry = countryOf(input.scenery, input.originIcao);
  const destCountry = countryOf(input.scenery, input.destIcao);
  if (!originCountry || !destCountry || originCountry === "—") return ok;
  if (originCountry.localeCompare(destCountry, undefined, { sensitivity: "accent" }) === 0) return ok;
  const stops = (input.stopIcaos ?? []).map((icao) => icaoKey(icao)).filter(Boolean);
  const route = [icaoKey(input.destIcao), ...stops];
  if (route.some((icao) => clearsCustoms(input.ops, icao))) return { ...ok, required: true };
  const origin = icaoKey(input.originIcao);
  const dest = icaoKey(input.destIcao);
  const suggested = input.scenery
    .map((field) => icaoKey(field.icao))
    .find((icao) => icao && icao !== origin && icao !== dest && clearsCustoms(input.ops, icao));
  const where = suggested ? ` Suggested customs stop ${suggested}.` : " Mark a customs-capable field in the airport manager.";
  return {
    required: true,
    ok: false,
    severity: input.ops.customsEnforcement === "block" ? "block" : "flag",
    message: `CUSTOMS  ${origin} (${originCountry})–${dest} (${destCountry}) needs a customs stop.${where}`,
    suggestedStopIcao: suggested,
  };
}

export type WeightLimits = {
  oewLb?: number;
  mzfwLb?: number;
  mtowLb?: number;
  maxPayloadLb?: number;
  maxFuelLb?: number;
};

export type WeightBallpark = {
  oewLb: number;
  paxBodyLb: number;
  includedBagLb: number;
  extraCargoLb: number;
  fuelLb: number;
  payloadLb: number;
  zfwLb: number;
  rampLb: number;
  paxWeightUnset: boolean;
  over: string[];
  severity: "ok" | OpsConstraintEnforcement;
  message: string;
};

function nonNeg(n: number | null | undefined): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Ramp ballpark = OEW + passenger body + SimBrief included bags + EXTRA cargo
 * + planned fuel. Ticket cargo at or under the bag ceiling is not stacked
 * on top of the included bags.
 */
export function ballparkTripWeight(input: {
  ops: Pick<OpsDeskSettings, "standardPaxLb" | "weightEnforcement">;
  limits: WeightLimits;
  paxCount: number;
  cargoLb: number;
  fuelLb: number;
  flightType?: string;
}): WeightBallpark {
  const pax = Number.isFinite(input.paxCount) ? Math.max(0, Math.round(input.paxCount)) : 0;
  const paxWeightUnset = input.ops.standardPaxLb <= 0 && pax > 0;
  const paxBodyLb = paxWeightUnset ? 0 : pax * input.ops.standardPaxLb;
  const includedBagLb = pax * SIMBRIEF_INCLUDED_BAG_LB;
  const extraCargoLb = simbriefExtraFreightLb(pax, input.cargoLb, input.flightType);
  const fuelLb = nonNeg(input.fuelLb);
  const oewLb = nonNeg(input.limits.oewLb);
  const payloadLb = paxBodyLb + includedBagLb + extraCargoLb;
  const zfwLb = oewLb + payloadLb;
  const rampLb = zfwLb + fuelLb;
  const over: string[] = [];
  const mtow = nonNeg(input.limits.mtowLb);
  const mzfw = nonNeg(input.limits.mzfwLb);
  const payloadLimit = nonNeg(input.limits.maxPayloadLb);
  const fuelLimit = nonNeg(input.limits.maxFuelLb);
  if (mtow > 0 && rampLb > mtow) over.push(`MTOW ${Math.round(rampLb)} lb > ${Math.round(mtow)} lb`);
  if (mzfw > 0 && zfwLb > mzfw) over.push(`MZFW ${Math.round(zfwLb)} lb > ${Math.round(mzfw)} lb`);
  if (payloadLimit > 0 && payloadLb > payloadLimit) over.push(`payload ${Math.round(payloadLb)} lb > ${Math.round(payloadLimit)} lb`);
  if (fuelLimit > 0 && fuelLb > fuelLimit) over.push(`fuel ${Math.round(fuelLb)} lb > ${Math.round(fuelLimit)} lb`);
  const unset = paxWeightUnset ? " Passenger body weight is unset in Settings — pax mass not included." : "";
  if (!over.length) {
    return {
      oewLb,
      paxBodyLb,
      includedBagLb,
      extraCargoLb,
      fuelLb,
      payloadLb,
      zfwLb,
      rampLb,
      paxWeightUnset,
      over,
      severity: "ok",
      message: unset.trim(),
    };
  }
  return {
    oewLb,
    paxBodyLb,
    includedBagLb,
    extraCargoLb,
    fuelLb,
    payloadLb,
    zfwLb,
    rampLb,
    paxWeightUnset,
    over,
    severity: input.ops.weightEnforcement === "block" ? "block" : "flag",
    message: `WEIGHT  ${over.join("; ")}.${unset}`,
  };
}

export type TripConstraintReport = {
  hits: ConstraintHit[];
  hard: ConstraintHit[];
  flags: string[];
  crew: CrewMinVerdict;
  curfew: CurfewVerdict;
  customs: CustomsVerdict;
  weight: WeightBallpark;
  deice: DeiceVerdict;
  convective: ConvectiveHoldVerdict;
};

function pushHit(hits: ConstraintHit[], severity: "ok" | OpsConstraintEnforcement, code: ConstraintCode, message: string) {
  if (severity === "ok" || !message) return;
  hits.push({ code, severity, message });
}

export function evaluateTripConstraints(input: {
  ops: OpsDeskSettings;
  aircraft?: Pick<AircraftSpec, "singlePilot" | "registration" | "oewLb" | "mzfwLb" | "mtowLb" | "maxPayloadLb" | "maxFuelLb"> | null;
  originIcao: string;
  destIcao: string;
  departure: Date | string | null;
  eteMin: number;
  scenery: readonly CustomsField[];
  stopIcaos?: readonly string[];
  crewIds: ReadonlyArray<string | null | undefined>;
  paxCount: number;
  cargoLb: number;
  fuelLb: number;
  flightType?: string;
  depField?: FieldWx | null;
  hazards?: AwcHazardBrief | null;
}): TripConstraintReport {
  const departure = input.departure instanceof Date
    ? input.departure
    : input.departure
      ? new Date(input.departure)
      : null;
  const crew = evaluateCrewMinimum({
    ops: input.ops,
    tailSinglePilot: input.aircraft?.singlePilot,
    crewIds: input.crewIds,
    registration: input.aircraft?.registration,
  });
  const curfew = evaluateCurfew({
    ops: input.ops,
    originIcao: input.originIcao,
    destIcao: input.destIcao,
    departure: departure && Number.isFinite(departure.getTime()) ? departure : null,
    eteMin: input.eteMin,
  });
  const customs = evaluateCustoms({
    ops: input.ops,
    originIcao: input.originIcao,
    destIcao: input.destIcao,
    scenery: input.scenery,
    stopIcaos: input.stopIcaos,
  });
  const weight = ballparkTripWeight({
    ops: input.ops,
    limits: input.aircraft ?? {},
    paxCount: input.paxCount,
    cargoLb: input.cargoLb,
    fuelLb: input.fuelLb,
    flightType: input.flightType,
  });
  const deice = evaluateDeice({
    ops: input.ops,
    depField: input.depField,
  });
  const convective = evaluateConvectiveHold({
    ops: input.ops,
    hazards: input.hazards,
    originIcao: input.originIcao,
    destIcao: input.destIcao,
  });
  const hits: ConstraintHit[] = [];
  pushHit(hits, crew.severity, "under_crewed", crew.message);
  pushHit(hits, curfew.severity, "curfew", curfew.message);
  pushHit(hits, customs.severity, "customs", customs.message);
  pushHit(hits, weight.severity, "overweight", weight.message);
  pushHit(hits, deice.severity, "deice", deice.message);
  pushHit(hits, convective.severity, "convective_hold", convective.message);
  return {
    hits,
    hard: hits.filter((hit) => hit.severity === "block"),
    flags: hits.filter((hit) => hit.severity === "flag").map((hit) => hit.message),
    crew,
    curfew,
    customs,
    weight,
    deice,
    convective,
  };
}

/** Keep ferry / overnight notes. Replace prior constraint lines from this module. */
export function mergeConstraintNotes(existing: string | null | undefined, notes: readonly string[]): string | undefined {
  const kept = String(existing ?? "")
    .split(" · ")
    .map((part) => part.trim())
    .filter((part) => part && !NOTE_MARKERS.some((marker) => part.startsWith(marker)));
  const next = [...kept, ...notes.map((note) => note.trim()).filter(Boolean)];
  return next.length ? next.join(" · ") : undefined;
}

export function writeAirportOps(
  list: readonly OpsAirportOps[],
  icao: string,
  row: OpsAirportOps | null,
): OpsAirportOps[] {
  const key = icaoKey(icao);
  const rest = list.filter((item) => item.icao !== key);
  if (!key || !row) return rest;
  const next: OpsAirportOps = { ...row, icao: key };
  if (!next.curfew && next.customsAvailable === undefined && !String(next.notes ?? "").trim()) return rest;
  return [...rest, next];
}

export function clockToMinutes(raw: string): number | null {
  const match = raw.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

export function minutesToClock(minutes: number): string {
  const clamped = ((Math.round(minutes) % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}
